import {
  CaretLeft,
  Check,
  CreditCard,
  NumberCircleThree,
  Wallet,
} from "@phosphor-icons/react";
import React, { useCallback, useState } from "react";
import { APIResponse } from "../../api";
import usePaymentCheckoutOptions from "../../hooks/usePaymentCheckoutOptions";
import useSelectPaymentOption, {
  Data,
} from "../../hooks/useSelectPaymentOptionForCheckout";
import { useNavigate, useParams } from "react-router-dom";
import { HashLoader } from "react-spinners";
import { toast } from "sonner";

const getPaymentOptionIcon = (code: string) => {
  if (code === "001") {
    return CreditCard;
  }

  if (code === "002") {
    return NumberCircleThree;
  }

  return Wallet;
};

const getPaymentOptionCode = (option: {
  code?: string | null;
  id?: string | null;
}) => option.code || option.id || "";

const getPaymentOptionCopy = (option: {
  code?: string | null;
  id?: string | null;
  name: string;
  description: string;
}) => {
  const code = getPaymentOptionCode(option);

  if (code === "003") {
    return {
      name: "Pay with Wallet",
      description: "Pay instantly using your wallet balance.",
    };
  }

  if (code === "002") {
    return {
      name: "Pay Later (Installments)",
      description: "Split this order into convenient installments.",
    };
  }

  if (code === "001") {
    return {
      name: "Pay with Card",
      description: "Pay instantly with your saved card.",
    };
  }

  return {
    name: option.name,
    description: option.description,
  };
};

const SelectPaymentMethod = () => {
  const navigate = useNavigate();

  const { sessionId } = useParams();
  const {
    paymentOptions,
    isLoading: loadingPaymentOptions,
    isError: isPaymentOptionsError,
    error: paymentOptionsError,
  } = usePaymentCheckoutOptions();

  const proceed = useCallback(
    (data: APIResponse<Data>) => {
      const currentOrderId = sessionId;

      if (currentOrderId) {
        const tag = data?.data?.original?.data?.option_data?.tag;
        const paymentData = data?.data?.original?.data;

        if (tag === "pay_later") {
          navigate(`/marketplace/checkout/${currentOrderId}/pay-later`, {
            state: { paymentData },
          });
        } else if (tag === "pay_now_card") {
          navigate(`/marketplace/checkout/${currentOrderId}/pay-now`, {
            state: { paymentData },
          });
        } else if (tag === "pay_now_wallet") {
          navigate(`/marketplace/checkout/${currentOrderId}/pay-now`, {
            state: { paymentData },
          });
        } else {
          toast.error("Unsupported payment option. Please try another option.");
        }
      }
    },
    [navigate, sessionId]
  );

  const {
    selectPaymentOption,
    selectingPaymentOption,
    isSelectingPaymentOptionError,
    selectPaymentOptionError,
  } = useSelectPaymentOption({
    onSuccess(data) {
      proceed(data);
    },
  });

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    string | null
  >(null);

  return (
    <>
      <div>
        {selectingPaymentOption && (
          <div className="fixed top-0 z-[99] left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
            <HashLoader />
          </div>
        )}
        {loadingPaymentOptions && (
          <div className="fixed top-0 z-[99] left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
            <HashLoader />
          </div>
        )}
        <div className="flex items-center justify-center gap-10 px-5 py-20 text-center flex-col">
          <div className="flex flex-col gap-2">
            <div className="font-semibold text-3xl">Select Payment Mode</div>
            <div className="text-gray-500">
              Choose how you want to pay for your order.
            </div>
          </div>
          <div className="c-groupedSelect c-groupedSelect--vertical">
            {paymentOptions?.data?.map((option) => {
              const optionCode = getPaymentOptionCode(option);
              const optionCopy = getPaymentOptionCopy(option);
              const Icon = getPaymentOptionIcon(optionCode);

              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setSelectedPaymentMethod(optionCode)}
                  className={`c-groupedSelect__option ${
                    selectedPaymentMethod === optionCode && "active"
                  }`}
                >
                  <div className="text-visaro-orange rounded-full p-3 bg-visaro-orange/10">
                    <Icon weight="bold" size={24} />
                  </div>
                  <div className="flex-1 flex-col text-left">
                    <div className="text-lg font-medium">{optionCopy.name}</div>
                    <div className="text-sm text-gray-700">
                      {optionCopy.description}
                    </div>
                  </div>
                  {selectedPaymentMethod === optionCode ? (
                    <div className="bg-visaro-orange p-2 rounded-full">
                      <Check className="text-white" weight="bold" size={16} />
                    </div>
                  ) : (
                    <div className="p-2 rounded-full">
                      <Check
                        className="text-white invisible"
                        weight="bold"
                        size={16}
                      />
                    </div>
                  )}
                </button>
              );
            })}

            {isPaymentOptionsError && (
              <div className="text-red-500 py-4 text-left">
                {paymentOptionsError?.message}
              </div>
            )}

            {isSelectingPaymentOptionError && (
              <div className="text-red-500 py-4 text-left">
                {selectPaymentOptionError?.message}
              </div>
            )}

            <br />
            <div className="flex justify-between gap-2 w-full">
              <button
                type="button"
                onClick={() => navigate("/home")}
                className="p-2 py-3 text-center text-deepBlue border px-6 rounded-xl font-medium flex gap-2 items-center border-gray-300"
              >
                <CaretLeft weight="bold" size={20} />
                <span>Back</span>
              </button>
              <button
                onClick={() => {
                  if (selectedPaymentMethod) {
                    selectPaymentOption({
                      session_id: sessionId,
                      payment_option_id: selectedPaymentMethod!,
                    });
                  }
                }}
                disabled={!selectedPaymentMethod}
                className="p-2 py-3 text-center bg-deepBlue text-white px-10 border-2 rounded-xl font-medium"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SelectPaymentMethod;
